import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import { ACTIVE, INACTIVE } from 'src/constants/EntityStatus';
import { useAppSelector } from 'src/store/store';
import { setSiteCapacityFormData } from 'src/store/updateSiteCapacitySlice/updateSiteCapacitySlice';
import { createSearchRegExp } from 'src/utils/searchUtils';

import ShiftList from '../shiftList/shiftList';

const SiteCapacityFormFields = () => {
  const dispatch = useDispatch();
  const { siteCapacityFormData, shifts } = useAppSelector((state) => state.updateSiteCapacityPage);
  const [shiftsDropdownSearchQuery, setShiftsDropdownSearchQuery] = useState();
  const searchRegExp = createSearchRegExp(shiftsDropdownSearchQuery);
  const matchedShiftsDropdownValues = shifts
    .map((shift) => ({ label: shift.shiftCode, value: shift.shiftCode }))
    .filter((option) => !shiftsDropdownSearchQuery || searchRegExp.test(option.label));

  // cache the initial state of shift Active/Inactive. For non-admins, only show activate button when this is Inactive
  const [cachedActiveStatus, _] = useState(siteCapacityFormData?.active);

  const isShiftActive = siteCapacityFormData?.active === ACTIVE;

  useEffect(() => {
    const shiftNames = siteCapacityFormData?.shiftNames || [];
    const myShifts = siteCapacityFormData?.shifts || [];
    if (shiftNames.length !== myShifts.length) {
      dispatch(
        setSiteCapacityFormData({
          ...siteCapacityFormData,
          shifts: shiftNames.map((shiftCode) => {
            const existing = myShifts.filter((s) => s?.shiftCode === shiftCode);
            return existing.length > 0 ? existing[0] : shifts.filter((s) => s.shiftCode === shiftCode)[0];
          }),
        }),
      );
    }
  }, [siteCapacityFormData?.shiftNames]);

  const setIsActive = (active: boolean) => {
    dispatch(
      setSiteCapacityFormData({
        ...siteCapacityFormData,
        active: active ? ACTIVE : INACTIVE,
      }),
    );
  };

  return (
    <Column spacing="400" alignmentVertical="top">
      <Row>
        <Toggle checked={isShiftActive} onChange={setIsActive} />
        <Text type="b300">Activated</Text>
      </Row>
      {cachedActiveStatus === ACTIVE && !isShiftActive && (
        <Alert type="error" title="WARNING: Job Deactivation Policy">
          ONLY deactivate jobs that DO NOT physically exist at your site. Deactivating jobs to avoid accommodations is a
          violation of policy and will result in escalation. Sites maintaining artificially small job profiles are being
          monitored.
        </Alert>
      )}
      <Column spacing="200">
        <Text type="b300">Available Shifts</Text>
        <Select
          disabled={!isShiftActive}
          searchQuery={shiftsDropdownSearchQuery}
          onSearch={setShiftsDropdownSearchQuery}
          placeholder="Add shifts available for the job"
          value={siteCapacityFormData?.shiftNames}
          onChange={(shiftNames) => {
            dispatch(setSiteCapacityFormData({ ...siteCapacityFormData, shiftNames }));
          }}
        >
          {matchedShiftsDropdownValues.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
          {!matchedShiftsDropdownValues.length ? (
            <Column alignmentVertical="center" spacing="300" spacingInset="500">
              <Text alignment="center">No results</Text>
            </Column>
          ) : null}
        </Select>
      </Column>
      <ShiftList />
    </Column>
  );
};

export default SiteCapacityFormFields;
